import React, { useEffect, useMemo, useState } from 'react';
import { Button, Menu } from '@mantine/core';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import {
    IconSquarePlus,
    IconCircleCheck,
    IconDotsCircleHorizontal,
    IconChevronDown,
} from '@tabler/icons-react';
import { orderBy } from 'lodash';
import dayjs from 'dayjs';

import {
    DashboardHeader,
    GraphCard,
    OverviewCard,
    SapInterfaceAlert,
} from '../../../components/common';
import RequestCardItem from '../../../components/requests/RequestCardItem';
import RequestDrawer from '../../../components/requests/RequestDrawer';
import RequestsTable from '../../../components/requests/RequestsTable';
import {
    RequestsInProgressChart,
    RequestsPerCategoryGraph,
    RequestsPerTypeGraph,
} from '../../../components/graphs';
import {
    GET_STATUS_COUNTS,
    GET_CATEGORY_STATS,
} from '../../../apollo/queries/priceChangeRequest';
import ActionsCard from './components/ActionsCard';
import { showErrorNotification } from '../../../notifications';
import { useAuth } from '../../../context/AuthProvider';
import DateRangePickerWithPresets from '../../../components/common/DateRangePickerWithPresets';
import {
    RequestDrawerProvider,
    useRequestDrawer,
} from '../../../context/RequestDrawerContext';
import { UserRole } from '../../../types/users';
import { checkUserRole } from '../../../utility/userRoles';
import { User } from '../../../codegen/graphql';

const MY_REQUESTS_DEFAULT_ROLES: UserRole[] = [
    UserRole.PROCUREMENT_MANAGER,
    UserRole.STORE_MANAGER,
    UserRole.DIVISIONAL_MANAGER,
    UserRole.OPS_EXECUTIVE,
];

const getDefaultUserRequestFilter = (authUser: User | null) => {
    if (checkUserRole(authUser, MY_REQUESTS_DEFAULT_ROLES)) {
        return 'My Requests';
    }
    return 'All Requests';
};

function HomePageContent() {
    const { authUser } = useAuth();
    const {
        open,
        close,
        isOpen,
        requestId: contextRequestId,
    } = useRequestDrawer();

    const [searchParams, setSearchParams] = useSearchParams({});

    const today = dayjs();
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
        today.subtract(30, 'days').toDate(),
        today.toDate(),
    ]);

    const [userRequestFilter, setUserRequestFilter] = useState(
        getDefaultUserRequestFilter(authUser),
    );

    const myRequestsUserId = useMemo(() => {
        if (userRequestFilter !== 'My Requests') {
            return null;
        }
        return authUser?.id ?? null;
    }, [userRequestFilter, authUser?.id]);

    const filterVariables = useMemo(
        () => ({
            input: {
                startDate: dateRange[0],
                endDate: dateRange[1],
                userId: myRequestsUserId,
            },
        }),
        [dateRange, myRequestsUserId],
    );

    const [
        getStatusCounts,
        {
            data: statusCountsData,
            loading: loadingStatusCounts,
            error: statusCountsError,
        },
    ] = useLazyQuery(GET_STATUS_COUNTS, {
        pollInterval: 60000,
        variables: filterVariables,
    });

    const [
        getCategoryStats,
        {
            data: categoryStatsData,
            loading: loadingCategoryStats,
            error: categoryStatsError,
        },
    ] = useLazyQuery(GET_CATEGORY_STATS, {
        pollInterval: 60000,
        variables: filterVariables,
    });

    const priceChangeStats = useMemo(() => {
        const statusCounts =
            statusCountsData?.getPriceChangeRequestStatusCounts;
        const categoryStats =
            categoryStatsData?.getPriceChangeRequestCategoryStats;
        return {
            actionedRequests: statusCounts?.actionedRequests ?? 0,
            decreasedRequests: categoryStats?.decreasedRequests ?? 0,
            inProgressRequests: statusCounts?.inProgressRequests ?? 0,
            increasedRequests: categoryStats?.increasedRequests ?? 0,
            newRequests: statusCounts?.newRequests ?? 0,
            pendingRequests: statusCounts?.pendingRequests ?? 0,
            requestsPerCategory: categoryStats?.requestsPerCategory ?? [],
            supplierNegotiationRequests:
                statusCounts?.supplierNegotiationRequests ?? 0,
            totalRequests: statusCounts?.totalRequests ?? 0,
        };
    }, [statusCountsData, categoryStatsData]);

    const priceChangeStatsError = statusCountsError || categoryStatsError;

    const sortedRequestsPerType = useMemo(() => {
        return orderBy(
            priceChangeStats.requestsPerCategory,
            ['totalRequests'],
            ['desc'],
        ).splice(0, 4);
    }, [priceChangeStats.requestsPerCategory]);

    const requestCardData = useMemo(
        () => [
            {
                title: 'New',
                count: priceChangeStats?.newRequests,
                icon: <IconSquarePlus color="#004D94" />,
                backgroundColor: '#EBF1FF',
            },
            {
                title: 'Actioned',
                count: priceChangeStats?.actionedRequests,
                icon: <IconCircleCheck color="#12B886" />,
                backgroundColor: '#E6FCF5',
            },
            {
                title: 'In Progress',
                count: priceChangeStats?.inProgressRequests,
                icon: <IconDotsCircleHorizontal color="#E8590C" />,
                backgroundColor: '#FFF9DB',
            },
        ],
        [priceChangeStats],
    );

    const requestId = useMemo(() => {
        return searchParams.get('requestId');
    }, [searchParams]);

    const highlightMyActionsFromDigest =
        searchParams.get('highlight') === 'my-actions';

    useEffect(() => {
        if (!highlightMyActionsFromDigest) {
            return () => {};
        }
        const timeoutId = window.setTimeout(() => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev);
                    next.delete('highlight');
                    return next;
                },
                { replace: true },
            );
        }, 10_000);
        return () => window.clearTimeout(timeoutId);
    }, [highlightMyActionsFromDigest, setSearchParams]);

    useEffect(() => {
        if (requestId && !isOpen) {
            open(null, requestId);
        }
    }, [requestId, isOpen, open]);

    useEffect(() => {
        if (requestId && isOpen && requestId !== contextRequestId) {
            open(null, requestId);
        }
    }, [requestId, isOpen, contextRequestId, open]);

    const closeRequestsDrawer = () => {
        close();
        setSearchParams({});
    };

    const datesButtonText = useMemo(() => {
        if (!dateRange[0] && !dateRange[1]) {
            return 'Select Date Range';
        }

        if (dateRange[0] && !dateRange[1]) {
            return `${dayjs(dateRange[0]).format('D MMM YYYY')}`;
        }

        if (!dateRange[0] && dateRange[1]) {
            return `${dayjs(dateRange[1]).format('D MMM YYYY')}`;
        }

        return `${dayjs(dateRange[0]).format('D MMM YYYY')} - ${dayjs(
            dateRange[1],
        ).format('D MMM YYYY')}`;
    }, [dateRange]);

    const graphLabels = useMemo(() => {
        return {
            requestsPerType: [
                {
                    color: 'orange',
                    label: 'Decrease',
                    value: priceChangeStats.decreasedRequests,
                },
                {
                    color: '#004d94',
                    label: 'Increase',
                    value: priceChangeStats.increasedRequests,
                },
            ],
            requestsProgress: [
                {
                    color: 'lightBlue',
                    label: 'Actioned',
                    value: priceChangeStats.actionedRequests,
                },
                {
                    color: 'orange',
                    label: 'Pending',
                    value: priceChangeStats.pendingRequests,
                },
                {
                    color: '#004d94',
                    label: 'Sup. Negotiation',
                    value: priceChangeStats.supplierNegotiationRequests,
                },
            ],
        };
    }, [priceChangeStats]);

    useEffect(() => {
        if (priceChangeStatsError) {
            showErrorNotification('Error fetching price change stats');
        }
    }, [priceChangeStatsError]);

    useEffect(() => {
        getStatusCounts();
        getCategoryStats();
    }, [dateRange, userRequestFilter, getStatusCounts, getCategoryStats]);

    return (
        <div className="relative">
            {false && <SapInterfaceAlert />}
            <DashboardHeader
                title="Overview"
                hasBreadcrumb={false}
                hasDate
                isLoading={false}
                rightComponent={
                    <div className="flex flex-1 items-center justify-end gap-4">
                        <DateRangePickerWithPresets
                            value={dateRange}
                            onChange={setDateRange}
                            buttonLabel={datesButtonText}
                        />
                        <Menu>
                            <Menu.Target>
                                <Button
                                    size="md"
                                    rightSection={<IconChevronDown size={16} />}
                                    fullWidth={false}
                                >
                                    {userRequestFilter}
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item
                                    onClick={() =>
                                        setUserRequestFilter('My Requests')
                                    }
                                >
                                    My Requests
                                </Menu.Item>
                                <Menu.Item
                                    onClick={() =>
                                        setUserRequestFilter('All Requests')
                                    }
                                >
                                    All Requests
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    </div>
                }
            />
            <div className="px-11 flex flex-col flex-1 gap-5">
                {/* OverviewCards */}
                <div className="grid grid-cols-2 gap-5">
                    <OverviewCard
                        title={`Requests [${priceChangeStats?.totalRequests}]`}
                        isLoading={loadingStatusCounts}
                        timeFrame={datesButtonText}
                    >
                        <div className="flex gap-4 justify-evenly w-full">
                            {requestCardData.map(data => (
                                <RequestCardItem key={data.title} {...data} />
                            ))}
                        </div>
                    </OverviewCard>
                    <ActionsCard
                        emphasizeFromDigest={highlightMyActionsFromDigest}
                    />
                </div>
                <div className="grid grid-cols-3 gap-5">
                    <GraphCard
                        title="Top Requests Per Category"
                        timeFrame={datesButtonText}
                        isLoading={loadingCategoryStats}
                    >
                        <RequestsPerCategoryGraph
                            requestsPerCategory={
                                priceChangeStats.requestsPerCategory
                            }
                        />
                    </GraphCard>
                    <GraphCard
                        title="Requests Per Type"
                        timeFrame={datesButtonText}
                        labels={graphLabels.requestsPerType}
                        isLoading={loadingCategoryStats}
                    >
                        <RequestsPerTypeGraph
                            requestsPerType={sortedRequestsPerType}
                        />
                    </GraphCard>
                    <GraphCard
                        title="Requests Progress"
                        timeFrame={datesButtonText}
                        isLoading={loadingStatusCounts}
                        labels={graphLabels.requestsProgress}
                    >
                        <RequestsInProgressChart
                            total={priceChangeStats.totalRequests}
                            actioned={priceChangeStats.actionedRequests}
                            pending={priceChangeStats.pendingRequests}
                            supplierNegotiation={
                                priceChangeStats.supplierNegotiationRequests
                            }
                        />
                    </GraphCard>
                </div>

                <div className="mb-11">
                    <RequestsTable
                        dateRange={dateRange}
                        userId={myRequestsUserId}
                        showDateRangeSelector={false}
                    />
                </div>
            </div>
            <RequestDrawer onClose={closeRequestsDrawer} />
        </div>
    );
}

function HomePage() {
    const { role } = useAuth();
    if (role === 'storemanager') {
        return <Navigate to="/price-change/requests" replace />;
    }
    return (
        <RequestDrawerProvider>
            <HomePageContent />
        </RequestDrawerProvider>
    );
}

export default HomePage;
